import { resolveFormId, setAppPrefix, trackFormLoad, trackFormValidationError, trackFormHandlerError } from './formAnalytics';
import { breadcrumb } from '../../packages/sentry';

const OPT_OUT_ATTRIBUTE = 'data-analytics';
const OFF = 'off';

function isOptedOut(vm) {
  return (vm.$attrs && vm.$attrs[OPT_OUT_ATTRIBUTE]) === OFF;
}

/**
 * Landmine: dropping this check double-reports every submit — a nested observer
 * validates whenever its parent does, and handleSubmit validates internally.
 */
function isRootObserver(vm) {
  return !vm.$_veeObserver;
}

function reports(vm) {
  return isRootObserver(vm) && !isOptedOut(vm);
}

function hasSubmitAffordance(vm) {
  const el = vm.$el;

  return Boolean(el && el.querySelector && el.querySelector('form, [type="submit"]'));
}

/**
 * Landmine: this runs at module scope, before `new Vue()`. Letting it throw turns a
 * missing analytics event into a blank page on every app it is installed in, so it
 * bails on an unrecognized vendor shape instead.
 */
export function installFormAnalytics(ValidationObserver, { app } = {}) {
  const options = ValidationObserver && ValidationObserver.options;
  const methods = options && options.methods;

  if (!methods || typeof methods.validate !== 'function' || typeof methods.handleSubmit !== 'function') {
    breadcrumb('form-analytics', 'error', 'vee-validate shape not recognized, analytics not installed', { app });

    return;
  }

  if (options.__dpFormAnalyticsInstalled) {
    return;
  }

  setAppPrefix(app);

  const { validate: originalValidate, handleSubmit: originalHandleSubmit } = methods;

  options.methods.validate = function(...args) {
    return originalValidate.apply(this, args).then(isValid => {
      const silent = Boolean(args[0] && args[0].silent);

      if (!isValid && !silent && !this.__dpSubmitDepth && reports(this)) {
        trackFormValidationError(this, this.errors);
      }

      return isValid;
    });
  };

  options.methods.handleSubmit = function(cb) {
    if (!reports(this)) {
      return originalHandleSubmit.call(this, cb);
    }

    let handlerCalled = false;

    const instrumented = cb
      ? () => {
          handlerCalled = true;
          return cb();
        }
      : cb;

    this.__dpSubmitDepth = (this.__dpSubmitDepth || 0) + 1;

    return originalHandleSubmit.call(this, instrumented).then(
      value => {
        this.__dpSubmitDepth -= 1;

        if (!handlerCalled) {
          trackFormValidationError(this, this.errors);
        }

        return value;
      },
      error => {
        this.__dpSubmitDepth -= 1;
        trackFormHandlerError(this, error);
        throw error;
      }
    );
  };

  options.mounted = [].concat(options.mounted || [], function() {
    if (reports(this) && hasSubmitAffordance(this) && resolveFormId(this)) {
      trackFormLoad(this);
    }
  });

  options.__dpFormAnalyticsInstalled = true;
}
